import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';

const THREE_MINUTES_MS = 3 * 60 * 1000;

const pendingNavTarget: { current: string | null } = { current: null };
let exitIntentShown = false;

export function clearPendingNavigation() {
  pendingNavTarget.current = null;
}

export function useFreeToPlayExitIntent() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { openModal } = useModal();
  const absoluteNavigate = useAbsoluteNavigate();
  // The exit-intent modal is a registration prompt — never show it in restricted mode.
  const isRestricted = useRestrictedMode();

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDismiss = useCallback(() => {
    if (pendingNavTarget.current) {
      const target = pendingNavTarget.current;
      clearPendingNavigation();
      absoluteNavigate(target);
    }
  }, [absoluteNavigate]);

  useEffect(() => {
    if (isAuthenticated || exitIntentShown || isRestricted) return;

    timerRef.current = setTimeout(() => {
      if (!exitIntentShown) {
        exitIntentShown = true;
        openModal(MODALS.FREE_TO_PLAY_EXIT_INTENT, { onClose: handleDismiss });
      }
    }, THREE_MINUTES_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      clearPendingNavigation();
    };
  }, [isAuthenticated, openModal, handleDismiss, isRestricted]);

  const interceptNavigation = useCallback(
    (targetPath: string): boolean => {
      if (isAuthenticated || isRestricted) return false;
      if (exitIntentShown) return false;

      exitIntentShown = true;
      pendingNavTarget.current = targetPath;
      openModal(MODALS.FREE_TO_PLAY_EXIT_INTENT, { onClose: handleDismiss });
      return true;
    },
    [isAuthenticated, openModal, handleDismiss, isRestricted],
  );

  return { interceptNavigation };
}
